'use client'

type CreditItem = {
  name: string
  credits: number
}

type PaymentSuccessModalProps = {
  isOpen: boolean
  attemptsAdded: number
  onContinue: () => void
  isContinueLoading?: boolean
  creditBreakdown?: CreditItem[] | null
  /** Individual plan label, e.g. "Mock Interview". Combo plans use creditBreakdown. */
  includedPlanName?: string
}

export function PaymentSuccessModal({
  isOpen,
  attemptsAdded,
  onContinue,
  isContinueLoading = false,
  creditBreakdown,
  includedPlanName,
}: PaymentSuccessModalProps) {
  if (!isOpen) return null

  const handleContinue = () => {
    if (isContinueLoading) return
    onContinue()
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-body">
          <div className="modal-icon-wrapper">
            <div className="credit-card-icon">
              <svg width="80" height="60" viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="4" y="12" width="72" height="44" rx="8" fill="#5B4EF5"/>
                <rect x="4" y="12" width="72" height="12" rx="8" fill="#6B5CE7"/>
                <rect x="12" y="32" width="56" height="4" rx="2" fill="#E5E7EB" opacity="0.3"/>
                <rect x="12" y="40" width="40" height="4" rx="2" fill="#E5E7EB" opacity="0.3"/>
              </svg>
              <div className="checkmark-overlay">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="12" fill="#10B981"/>
                  <path d="M7 12L10 15L17 8" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>

          <div className="modal-text-content">
            <h2 className="modal-heading">Payment successful</h2>

            <p className="modal-paragraph">
              Your payment has been completed successfully.{' '}
              {creditBreakdown && creditBreakdown.length > 0
                ? "Your combo plan credits have been added to your account."
                : `You've unlocked ${attemptsAdded} new interview attempt${attemptsAdded !== 1 ? 's' : ''} giving you more chances to practice, refine your answers, and improve your performance.`}
            </p>

            <div className="summary-section">
              <h3 className="summary-heading">What&apos;s included:</h3>
              {creditBreakdown && creditBreakdown.length > 0 ? (
                <ul className="credit-list">
                  {creditBreakdown.map((item) => (
                    <li key={item.name} className="summary-item">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="10" cy="10" r="10" fill="#10B981"/>
                        <path d="M6 10L9 13L14 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span><strong>{item.credits}</strong> {item.credits !== 1 ? `${item.name}s` : item.name}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="summary-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="10" fill="#10B981"/>
                    <path d="M6 10L9 13L14 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>
                    {includedPlanName ? (
                      <>
                        <strong>1</strong> {includedPlanName}
                      </>
                    ) : (
                      <>New attempts added: {attemptsAdded}</>
                    )}
                  </span>
                </div>
              )}
            </div>

            <p className="modal-paragraph">
              You&apos;re all set to continue your interview journey with meritlens
            </p>

            <p className="encouragement-text">Keep up the great work 🚀</p>

            <button
              className={`continue-button${isContinueLoading ? ' continue-button-loading' : ''}`}
              onClick={handleContinue}
              disabled={isContinueLoading}
              aria-busy={isContinueLoading}
            >
              {isContinueLoading ? (
                <span className="continue-button-inner">
                  <span className="continue-spinner" aria-hidden />
                  Starting interview...
                </span>
              ) : creditBreakdown && creditBreakdown.length > 0 ? (
                'Continue'
              ) : (
                'Continue to Interview'
              )}
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1002;
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .modal-content {
          background: white;
          border-radius: 24px;
          padding: 3rem;
          max-width: 580px;
          width: 90%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.3s ease-out;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal-body {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          text-align: left;
        }

        .modal-icon-wrapper {
          margin-bottom: 2rem;
        }

        .credit-card-icon {
          position: relative;
          width: 80px;
          height: 60px;
        }

        .checkmark-overlay {
          position: absolute;
          top: -8px;
          right: -8px;
          z-index: 10;
        }

        .modal-text-content {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .modal-heading {
          font-size: 1.75rem;
          font-weight: 700;
          color: #5B4EF5;
          margin: 0 0 1.25rem 0;
          line-height: 1.3;
        }

        .modal-paragraph {
          font-size: 1rem;
          color: #000000;
          line-height: 1.6;
          margin: 0 0 1.5rem 0;
          max-width: 460px;
        }

        .summary-section {
          width: 100%;
          margin-bottom: 1.5rem;
        }

        .summary-heading {
          font-size: 1rem;
          font-weight: 700;
          color: #000000;
          margin: 0 0 0.75rem 0;
        }

        .summary-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9375rem;
          color: #000000;
        }

        .encouragement-text {
          font-size: 1rem;
          font-weight: 700;
          color: #000000;
          margin: 0 0 2rem 0;
        }

        .continue-button {
          width: 100%;
          padding: 0.875rem 2rem;
          background: linear-gradient(135deg, #6B5CE7 0%, #5B4EF5 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(91, 78, 245, 0.3);
        }

        .continue-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(91, 78, 245, 0.4);
          background: linear-gradient(135deg, #7C6EE8 0%, #6B5CE7 100%);
        }

        .continue-button:active {
          transform: translateY(0);
        }

        .continue-button:disabled {
          cursor: not-allowed;
          opacity: 0.85;
          transform: none;
        }

        .continue-button-loading:hover {
          transform: none;
          box-shadow: 0 4px 12px rgba(91, 78, 245, 0.3);
          background: linear-gradient(135deg, #6B5CE7 0%, #5B4EF5 100%);
        }

        .continue-button-inner {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.625rem;
        }

        .continue-spinner {
          width: 1.125rem;
          height: 1.125rem;
          border: 2px solid rgba(255, 255, 255, 0.35);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .credit-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        @media (max-width: 640px) {
          .modal-content {
            padding: 2rem 1.5rem;
            max-width: 95%;
          }

          .modal-icon-wrapper {
            margin-bottom: 1.5rem;
          }

          .credit-card-icon {
            width: 60px;
            height: 45px;
          }

          .checkmark-overlay svg {
            width: 20px;
            height: 20px;
          }

          .modal-heading {
            font-size: 1.5rem;
          }

          .modal-paragraph {
            font-size: 0.9375rem;
          }

          .continue-button {
            padding: 0.875rem 1.5rem;
          }
        }
      `}</style>
    </div>
  )
}

